import { useCallback } from "react";
import { config } from "../config";
import { useEventsApi } from "./useEventsApi";
import type {
  ActionTextSuggestionRow,
  WordingGuardAnalysisResponse,
  WordingGuardAnalysisScope,
  WordingReplacementRow,
} from "../shared/wordingGuard";

type WordingPayload = {
  source_words: string[];
  target_word: string;
  language_code: string;
  is_active: boolean;
};

type ActionSuggestionPayload = {
  text: string;
  language_code: string;
  is_active: boolean;
  sort_order: number;
};

export const useWordingGuardApi = () => {
  const { callApi } = useEventsApi();

  const listWordingRules = useCallback(async (): Promise<WordingReplacementRow[]> => {
    const url = new URL(`${config.apiUrl}/api/wording-replacements/`);
    // Backend defaults `active` to true for list; admin UI must see inactive rules too.
    url.searchParams.set("active", "all");
    const response = await callApi(url.toString(), "GET");
    const rows = Array.isArray(response) ? response : response?.results;
    return Array.isArray(rows) ? (rows as WordingReplacementRow[]) : [];
  }, [callApi]);

  const createWordingRule = useCallback(async (payload: WordingPayload): Promise<WordingReplacementRow> => {
    return callApi(`${config.apiUrl}/api/wording-replacements/`, "POST", payload);
  }, [callApi]);

  const updateWordingRule = useCallback(async (id: number, payload: WordingPayload): Promise<WordingReplacementRow> => {
    return callApi(`${config.apiUrl}/api/wording-replacements/${id}/`, "PUT", payload);
  }, [callApi]);

  const deleteWordingRule = useCallback(async (id: number): Promise<void> => {
    await callApi(`${config.apiUrl}/api/wording-replacements/${id}/`, "DELETE");
  }, [callApi]);

  const analyzeWordingGuard = useCallback(
    async (scope: WordingGuardAnalysisScope): Promise<WordingGuardAnalysisResponse> => {
      const url = new URL(`${config.apiUrl}/api/wording-replacements/analyze/`);
      url.searchParams.set("scope", scope);
      const response = await callApi(url.toString(), "GET");
      return response as WordingGuardAnalysisResponse;
    },
    [callApi]
  );

  const applyWordingToDescription = useCallback(
    async (descriptionId: number): Promise<{ replacements: number }> => {
      return callApi(`${config.apiUrl}/api/wording-replacements/apply-to-description/`, "POST", {
        description_id: descriptionId,
      });
    },
    [callApi]
  );

  const listActionSuggestions = useCallback(
    async (params?: { languageCode?: string; active?: "true" | "false" | "all" }): Promise<ActionTextSuggestionRow[]> => {
      const url = new URL(`${config.apiUrl}/api/action-text-suggestions/`);
      if (params?.languageCode?.trim()) {
        url.searchParams.set("language_code", params.languageCode.trim());
      }
      url.searchParams.set("active", params?.active ?? "all");
      const response = await callApi(url.toString(), "GET");
      const rows = Array.isArray(response) ? response : response?.results;
      return Array.isArray(rows) ? (rows as ActionTextSuggestionRow[]) : [];
    },
    [callApi]
  );

  const createActionSuggestion = useCallback(
    async (payload: ActionSuggestionPayload): Promise<ActionTextSuggestionRow> => {
      return callApi(`${config.apiUrl}/api/action-text-suggestions/`, "POST", payload);
    },
    [callApi]
  );

  const updateActionSuggestion = useCallback(
    async (id: number, payload: ActionSuggestionPayload): Promise<ActionTextSuggestionRow> => {
      return callApi(`${config.apiUrl}/api/action-text-suggestions/${id}/`, "PUT", payload);
    },
    [callApi]
  );

  const deleteActionSuggestion = useCallback(async (id: number): Promise<void> => {
    await callApi(`${config.apiUrl}/api/action-text-suggestions/${id}/`, "DELETE");
  }, [callApi]);

  return {
    listWordingRules,
    createWordingRule,
    updateWordingRule,
    deleteWordingRule,
    analyzeWordingGuard,
    applyWordingToDescription,
    listActionSuggestions,
    createActionSuggestion,
    updateActionSuggestion,
    deleteActionSuggestion,
  };
};
